import { DocumentNode, gql, InMemoryCache, TypedDocumentNode } from '@apollo/client';

import { awsDateField, connectionField, onSubscriptionData as onSubscriptionDataBase, subscribe as subscribeBase, TSubscriptionMapping } from '@chocolate-soup-inc/cs-api-consumer-utils';
import {
  AddressFieldsFragmentDoc,
  BasicGiftFieldsFragmentDoc,
  CompanyFieldsFragmentDoc,
  ContactFieldsFragmentDoc,
  DependantFieldsFragmentDoc,
  DependantFieldsHydratedFragmentDoc,
  EmployeeFieldsFragmentDoc,
  EventFieldsFragmentDoc,
  GiftEventFieldsFragmentDoc,
  GiftFieldsFragmentDoc,
  ListCompanyDependantsDocument,
  ListCompanyEmployeesDocument,
  ListCompanyUpcomingEventsDocument,
  ListEmployeeAndDependatsGiftsDocument,
  ListEmployeeDependantsDocument,
  ListOfficesDocument,
  ListShipmentsDocument,
  ListUsersDocument,
  OfficeFieldsFragmentDoc,
  RecipientFieldsFragmentDoc,
  ShipmentFieldsFragmentDoc,
  TDependant,
  TEmployee,
  TEvent,
  TGift,
  TOffice,
  TShipment,
  TUser,
  UserFieldsFragmentDoc
} from '../../generated/graphql';
import { privateClient } from './api';

const CompanyFieldsWithDependenciesFragmentDoc = gql`
  ${CompanyFieldsFragmentDoc}
  ${ContactFieldsFragmentDoc}
  ${AddressFieldsFragmentDoc}
`;

export const OfficeFieldsWithDependenciesFragmentDoc = gql`
  ${OfficeFieldsFragmentDoc}
  ${ContactFieldsFragmentDoc}
  ${AddressFieldsFragmentDoc}
`;

export const EmployeeFieldsWithDependenciesFragmentDoc = gql`
  ${EmployeeFieldsFragmentDoc}
  ${OfficeFieldsWithDependenciesFragmentDoc}
`;

export const GiftFieldsWithDependenciesFragmentDoc = gql`
  ${GiftFieldsFragmentDoc}
  ${BasicGiftFieldsFragmentDoc}
  ${GiftEventFieldsFragmentDoc}
  ${RecipientFieldsFragmentDoc}
  ${DependantFieldsHydratedFragmentDoc}
  ${DependantFieldsFragmentDoc}
  ${EmployeeFieldsWithDependenciesFragmentDoc}
`;

export const ShipmentFieldsWithDependenciesFragmentDoc = gql`
  ${ShipmentFieldsFragmentDoc}
  ${GiftFieldsWithDependenciesFragmentDoc}
`;

export const cache = new InMemoryCache({
  possibleTypes: {
    Recipient: ['Employee', 'Dependant']
  },
  typePolicies: {
    Query: {
      fields: {
        listOffices: connectionField<TOffice>({ keyArgs: ['companyId'] }),
        listUsers: connectionField<TUser>({ keyArgs: ['companyId'] }),
        listCompanyEmployees: connectionField<TEmployee>({ keyArgs: ['companyId'] }),
        listClientOneOffEvents: connectionField<TEvent>({
          // The API derives tenant scope from auth; companyId only partitions the client cache.
          keyArgs: ['companyId', 'eventPage', 'includingLastDays', 'includingNextDays', 'filter', 'order', 'isScan']
        }),
        listEmployeeDependants: connectionField<TDependant>({ keyArgs: ['companyId', 'employeeId'] }),
        listEmployeeAndDependatsGifts: connectionField<TGift>({ keyArgs: ['companyId', 'employeeId'] }),
        listCompanyShipments: connectionField<TShipment>({ keyArgs: ['companyId'] })
      }
    },
    User: {
      keyFields: ['email']
    },
    Company: {
      fields: {
        startDate: awsDateField(),
        endDate: awsDateField()
      }
    },
    Employee: {
      fields: {
        birthDate: awsDateField(),
        giftPausedFrom: awsDateField(),
        giftPausedTo: awsDateField(),
        hireDate: awsDateField()
      }
    },
    Dependant: {
      fields: {
        birthDate: awsDateField()
      }
    }
  }
});

const subscriptionMappings: Record<string, TSubscriptionMapping> = {
  onCompanyChanged: {
    listQueries: [],
    fragment: CompanyFieldsWithDependenciesFragmentDoc
  },
  onCompanyOfficeChanged: {
    listQueries: [ListOfficesDocument],
    fragment: OfficeFieldsWithDependenciesFragmentDoc
  },
  onCompanyUserChanged: {
    listQueries: [ListUsersDocument],
    fragment: UserFieldsFragmentDoc
  },
  onCompanyEmployeeChanged: {
    listQueries: [ListCompanyEmployeesDocument],
    fragment: EmployeeFieldsWithDependenciesFragmentDoc
  },
  onCompanyDependantChanged: {
    listQueries: [ListEmployeeDependantsDocument, ListCompanyDependantsDocument],
    fragment: DependantFieldsFragmentDoc
  },
  onCompanyGiftChanged: {
    listQueries: [ListEmployeeAndDependatsGiftsDocument],
    fragment: GiftFieldsWithDependenciesFragmentDoc
  },
  onCompanyShipmentChanged: {
    listQueries: [ListShipmentsDocument],
    fragment: ShipmentFieldsWithDependenciesFragmentDoc
  },
  onCompanyEventChanged: {
    listQueries: [ListCompanyUpcomingEventsDocument],
    fragment: EventFieldsFragmentDoc
  }
};

export const onSubscriptionData = onSubscriptionDataBase({
  cache,
  subscriptionMappings,
  debug: import.meta.env.REACT_APP_STAGE != 'production'
});

export type TSubscribeData = {
  query: DocumentNode | TypedDocumentNode;
  variables: Record<string, any>;
};

export const subscribe = ({ query, variables }: TSubscribeData) => {
  return subscribeBase(
    {
      client: privateClient,
      query,
      onSubscriptionData,
      variables
    },
    import.meta.env.REACT_APP_STAGE != 'production'
  );
};
